import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
} from '@angular/forms';
import { IMAGE_MAX_SIZES } from '@epicuro-next/ataraxia/image';
import { variantInOpenedListLocale } from '@epicuro-next/constants/features/localization';
import * as configStripState from '@epicuro-next/state/config-strip-state';
import { selectBackOrderEnabledRule } from '@epicuro-next/state/config-strip-state';
import { ProductListActionsBaseComponentStore } from '@epicuro-next/state/product-list-state';
import { select, Store } from '@ngrx/store';
import { Product } from 'graphql-api/gql-api-interfaces';
import { BehaviorSubject, map } from 'rxjs';

import {
  backOrdersOnly,
  getCellInventories,
} from '../../../shared/cell/cell-inventory';
import {
  BaseSizeGridComponent,
  CellPosition,
} from '../base-size-grid.component';

@Component({
  selector: 'epc-vertical-size-grid',
  templateUrl: './vertical-size-grid.component.html',
  styleUrls: ['./vertical-size-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class VerticalSizeGridComponent
  extends BaseSizeGridComponent
  implements OnChanges, OnDestroy, OnInit
{
  public imageMaxSizes = IMAGE_MAX_SIZES;
  public variantInOpenedListLocale = variantInOpenedListLocale;
  public selectedYSize$ = new BehaviorSubject<string | undefined>(undefined);
  public selectedMaterial$ = new BehaviorSubject<Product | undefined>(
    undefined,
  );

  private sizeRunsForm: UntypedFormGroup;

  public readonly backOrdersOnly = backOrdersOnly;

  private readonly backOrderEnabled$ = this.store.pipe(
    select(selectBackOrderEnabledRule),
  );

  public backOrderNotEnabled$ = this.backOrderEnabled$.pipe(
    map((isBackOrderEnabled) => !isBackOrderEnabled),
  );

  constructor(
    fb: UntypedFormBuilder,
    public cdr: ChangeDetectorRef,
    componentStore: ProductListActionsBaseComponentStore,
    private store: Store,
  ) {
    super(fb, cdr, componentStore);
  }

  public ngOnInit() {
    this.sizeRunsForm = this.fb.group(
      this.materials.reduce(
        (acc: Record<string, unknown>, material: Product) => {
          acc[material.productId] = this.fb.control(
            this.getSizeRunsQuantity(material.productId),
          );

          return acc;
        },
        {},
      ),
    );
  }

  public ngOnChanges(changes: SimpleChanges) {
    super.ngOnChanges(changes);

    if (
      changes.preSelectedMaterial &&
      changes.preSelectedMaterial.previousValue !== this.preSelectedMaterial
    ) {
      const material = this.materials.find(
        (m) => m.productId === changes.preSelectedMaterial.currentValue,
      );
      this.selectedMaterial$.next(material);
    }

    if (
      (changes.materials ||
        changes.sizesInventories ||
        changes.deliveryShop ||
        changes.sizeRunsSizes) &&
      this.sizeRunsForm
    ) {
      const sizeRunValue = this.materials.reduce(
        (acc: Record<string, unknown>, material: Product) => {
          acc[material.productId] = this.getSizeRunsQuantity(
            material.productId,
          );

          return acc;
        },
        {},
      );
      this.sizeRunsForm.reset(sizeRunValue);
    }
  }

  public ngOnDestroy() {
    super.ngOnDestroy();
  }

  public updateQuantitiesFormGroup(formGroup: UntypedFormGroup, value: number) {
    formGroup.get('quantity')?.patchValue(value);
    this.cdr.detectChanges();
  }

  public onMaterialClick(materialId: string) {
    super.onMaterialClick(materialId);
    const material = this.materials.find((m) => m.productId === materialId);
    this.selectedMaterial$.next(material);
    this.store.dispatch(
      configStripState.selectSize({
        sizeId: null as unknown as string,
        variantId: materialId ?? null,
      }),
    );
  }

  public selectScale(scale: string) {
    super.selectScale(scale);
    if (this.ySizes) this.selectedYSize$.next(this.ySizes[0]);
  }

  public get selectedMaterialGroup() {
    return this.formGroup.get(this.selectedMaterialId) as UntypedFormArray;
  }

  public get selectedYSizeGroup() {
    return this.selectedMaterialGroup.controls[
      this.ySizes?.indexOf(this.selectedYSize$.value as string) ?? 0
    ] as UntypedFormArray;
  }

  public get selectedSizeRun() {
    return this.sizeRunsForm.get(this.selectedMaterialId) as UntypedFormArray;
  }

  public get selectedYSizeIndex() {
    return this.ySizes?.indexOf(this.selectedYSize$.value as string) ?? 0;
  }

  public getCellInventories(xSize: string) {
    const { material, row, column } = this.mapXsizeToCell(xSize);
    if (!material?.productId) return { totals: 0, inventories: [] };
    return getCellInventories(this.materialsMatrix, this.sizesInventories)(
      material?.productId,
      row,
      column,
    );
  }

  public selectCell(xSize: string) {
    if (!this.getCellInventories(xSize)?.totals) return;
    const { material, row, column } = this.mapXsizeToCell(xSize);
    if (!material?.productId) return;
    return super.selectCell(material?.productId, row, column);
  }

  public isCellActive(xSize: string) {
    const { material, row, column } = this.mapXsizeToCell(xSize);
    return isSameCell(this.activeCell, {
      materialId: material?.productId,
      row,
      column,
    });
  }

  public getQuantity(xSize: string) {
    const { material, row, column } = this.mapXsizeToCell(xSize);
    if (!material) return 0;

    return this.getInBasketQuantity(material.productId, row, column);
  }

  public isSizeHasConsignment(xSize: string) {
    if (!this.getCellInventories(xSize).totals) return;
    const { material, row, column } = this.mapXsizeToCell(xSize);
    const productId = material?.productId;
    if (!productId) return;
    return super.isCellHasConsignment(productId, row, column);
  }

  public onSizeRunsQuantityChange(materialId: string, quantity: number) {
    // tslint:disable-next-line:no-any
    this.selectedSizeRun.patchValue(quantity as any, { emitEvent: false });
    if (quantity === 0) {
      this.selectedYSizeGroup.controls.forEach((control) =>
        control.get('quantity')?.patchValue(0),
      );
      return;
    }

    const targetData = (this.getMaterialData(materialId) ?? [])[0];
    if (!targetData || targetData?.[0]?.isLocked) {
      return;
    }

    this.selectedYSizeGroup.controls.forEach((control, index) => {
      control
        .get('quantity')
        ?.patchValue((this.sizeRunsSizes[index] ?? 0) * quantity);
    });

    this.refreshData();
  }

  public getXIndex(xSize: string): number {
    return this.xSizes?.indexOf(xSize) ?? 0;
  }

  private mapXsizeToCell(xSize: string) {
    return {
      material: this.selectedMaterial$.value,
      row: this.selectedYSizeIndex,
      column: this.getXIndex(xSize),
    };
  }

  private get selectedMaterialId() {
    return this.selectedMaterial$.value?.productId as string;
  }
}

function isSameCell(
  cellA: CellPosition | undefined,
  cellB: CellPosition | undefined,
) {
  return (
    cellA?.materialId === cellB?.materialId &&
    cellA?.column === cellB?.column &&
    cellA?.row === cellB?.row
  );
}
